// tslint:disable: no-inferrable-types
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import * as FRYWorkflows from '@fry/booking/lib/workflow';
import { BuilderEvaluatorService } from '@fry/components/common/form-builder/evaluators.service';
import { RolesQuery, RolesStore } from '@fry/lib/roles';
import { UserFieldsStore } from '@fry/lib/user-fields';

import { BackendIntegrationSerializer } from './backend-integration-serializer';
import {
  FormFieldTypeGeneral,
  FormFieldTypeUserField
} from './form-field-type.model';
import { TransitionAction, TransitionActionSelectOptionDataSource } from './transition-action.model';
import { TransitionGuard } from './transition-guard.model';
import { Workflow } from '../lib/workflow.model';

/**
 * User type guard to verify the object is/behaves like BackendObjectRef
 *
 * @param obj Object to verify
 */
export function isBackendObjectRef(obj: any): obj is BackendObjectRef {
  return (obj as BackendObjectRef).id !== undefined
         && (obj as BackendObjectRef).name !== undefined;
}

/**
 * Reference on the Backend object
 *
 * Returned by the integration representing the backend object providing the
 * ID and the NAME of the object.
 */
export interface BackendObjectRef {
  id: string;
  name: string;
}

/**
 * Main and only point to communicate with the EAS & EAS Backend
 *
 * The responsibility of the Integration service is to provide access
 * to the EAS backend and EAS data structures to the Workflow Editor.
 *
 * Integration, together with the `BackendIntegrationSerializer` loads and
 * translates the EAS Workflow into the Workflow Editor workflow.
 *
 * @note Since local pools the editor is directly provided either a
 *       payload of the EAS Workflow JSON in case of editing or
 *       null|undefined in case of creation.
 *
 *       Therefore the Integration functionality loading/saving the workflow
 *       is not necessary and the integration serves only purpose to load required
 *       dependencies such as fields/user roles etc...
 */
@Injectable()
export class BackendIntegrationService {

  /**
   * Is Integration ready to use?
   *
   * This is true when all dependencies has been successful loaded otherwise
   * it's false, see `prepare()`.
   */
  public get isReady() { return this.isReady$$.value; }
  public get isReady$() { return this.isReady$$.asObservable(); }
  private isReady$$ = new BehaviorSubject<boolean>(false);

  /**
   * Is Integration busy saving/loading?
   *
   * This is true when there are some operations in progress otherwise false.
   */
  public get isBusy() { return this.isBusy$$.value; }
  public get isBusy$() { return this.isBusy$$.asObservable(); }
  private isBusy$$ = new BehaviorSubject<boolean>(false);

  /**
   * User roles available on the Backend
   */
  public get availableRoles() { return this.availableRoles$$.value; }
  public get availableRoles$() { return this.availableRoles$$.asObservable(); }
  private availableRoles$$ = new BehaviorSubject<BackendObjectRef[]>([]);

  /**
   * Form actions available on the Backend
   */
  private _availableActions: TransitionAction[] = [];
  public get availableActions(): TransitionAction[] { return this._availableActions; }

  private initializeActions(): void  {
    const sources: Record<TransitionActionSelectOptionDataSource, BackendObjectRef[]> =
      { 'availableRoles': this.availableRoles }
    this._availableActions =
      TransitionAction.AvailableActions
        .map(action => action.evaluateOptionCategoriesFromDataSource(sources))
        .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Form guards available on the Backend
   */
  public get availableGuards(): TransitionGuard[] {
    return TransitionGuard.AvailableGuards;
  }

  /**
   * Form User fields available on the Backend
   */
  public get availableUserFields() { return this.availableUserFields$$.value; }
  public get availableUserFields$() { return this.availableUserFields$$.asObservable(); }
  private availableUserFields$$ = new BehaviorSubject<FormFieldTypeUserField[]>([]);

  /**
   * Form General fields available on the Backend
   */
  public get availableGeneralFields() { return this.availableGeneralFields$$.value; }
  public get availableGeneralFields$() { return this.availableGeneralFields$$.asObservable(); }
  private availableGeneralFields$$ = new BehaviorSubject<FormFieldTypeGeneral[]>([]);

  /**
   * ORIGINAL EAS workflow payload from the Backend or new blank one
   *
   * We keep the original workflow document editor is initialised with to be able to
   * detect changes or revert.
   */
  public workflow: FRYWorkflows.Workflow;

  public constructor(private rolesStore: RolesStore,
                     private userFieldsStore: UserFieldsStore,
                     private workflowStore: FRYWorkflows.WorkflowStore,
                     private formFieldEvaluatorService: BuilderEvaluatorService) {
    this.rolesStore = rolesStore;
    this.userFieldsStore = userFieldsStore;
    this.workflowStore = workflowStore;
    this.formFieldEvaluatorService = formFieldEvaluatorService;

    // Integration starts with the blank Workflow to avoid optional
    // on the `this.workflow`.
    this.workflow = this.workflowStore.blank();
  }

  //
  // Service state & data
  //

  /**
   * Prepares the Backend service (and data) for the editor.
   *
   * When the data and service is ready the `isReady` flag is set to true.
   *
   * This method should be always called somewhere at the bootstrapping phase of the
   * editor. See `reset()` for ways how to re-initialise.
   *
   * @param workflow Data to initialise Workflow:
   *                 ID of the workflow to load from the API
   *                 WorkflowDocFormSchema payload in case we are editing the workflow
   *                 undefined to initialise new blank workflow
   */
  public prepare(workflow: FRYWorkflows.WorkflowDocFormSchema|string|undefined): Observable<boolean> {
    if (this.isReady) { this.reset(); }
    this.isBusy$$.next(true);

    try {
      return forkJoin([
        this.createWorkflow$(workflow),
        this.createRoles$(),
        this.createUserFields$(),
        this.createGeneralFields$(),
      ])
      .pipe(
        map(results => {
          if (!results[0] || !results[1] || !results[2] || !results[3]) {
            this.isReady$$.next(false);
            return false;
          }

          this.workflow = results[0];
          this.availableRoles$$.next(results[1]);
          this.availableUserFields$$.next(results[2]);
          this.availableGeneralFields$$.next(results[3]);

          this.initializeActions();
          this.isReady$$.next(true);
          this.isBusy$$.next(false);
          return true;
        })
      );
    } catch (error) {
      this.isBusy$$.next(false);
      return throwError(error);
    }
  }

  /**
   * Resets the service data & state, making it ready for the initialization
   * using `prepare()`.
   */
  public reset() {
    this.isReady$$.next(false);
    this.isBusy$$.next(false);
    this.availableRoles$$.next([]);
    this.availableUserFields$$.next([]);
    this.availableGeneralFields$$.next([]);
    this.workflow = this.workflowStore.blank();
  }

  //
  // Persistance
  //
  // Since local pools there is no persistence implemented within the Workflow
  // editor as the "parent" editors are responsible for persisting it.
  //
  // Workflow editory only pushes it's payload upstream, which is done in `EditorWorkflowDialog`.

  public updateSerializedWorkflow(workflow: Workflow): void {
    this.workflow.doc.name = workflow.name;
    this.workflow.doc.form = BackendIntegrationSerializer.serializeWorkflow(workflow);
  }

  //
  // Observable factories
  //

  private createRoles$(): Observable<BackendObjectRef[]> {
    const query: RolesQuery = { sort: true };
    return this.rolesStore.allWithSystem(query)
               .pipe(
                  map(roles =>
                    roles.map(role => BackendIntegrationSerializer.deserializeBackendObjectRef(role))
                  )
               );
  }

  private createWorkflow$(value: FRYWorkflows.WorkflowDocFormSchema|string|undefined): Observable<FRYWorkflows.Workflow> {
    // We create completely new workflow
    if (value === undefined) { return of(this.workflowStore.blank()); }

    // We need to load the workflow from the backend (at the moment not used)
    if (typeof value === 'string') {
      return this.workflowStore.get(value);
    }

    // Creates the FRYWorkflows.Workflow object and populates it with required info so that WorkflowEditor
    // is can serialize/deserialize the payload.
    //
    // This hack is here since introduction of the Local pools when Booking Item editor no longer provides
    // FRYWorkflows.Workflow but only FRYWorkflows.WorkflowDocFormSchema.
    //
    // TODO: Should Workflow editor as a whole work only with FRYWorkflows.WorkflowDocFormSchema?
    //       To avoid this hackery?
    const hack = this.workflowStore.blank();
    hack.id = value.id;
    hack.doc.form = value;
    hack.doc['_rev'] = 'hacked';
    hack.doc['name'] = value.name;
    return of(hack);
  }

  private createUserFields$(): Observable<FormFieldTypeUserField[]> {
    return this.userFieldsStore
               .all(true)
               .pipe(
                 map(fields =>
                   fields.map(field => BackendIntegrationSerializer.deserializeUserFieldType(field))
                 )
               );
  }

  /**
   * Fetches the known General fields from the system
   *
   * This observable also filters the field types which are not supported by
   * the editor.
   */
  private createGeneralFields$(): Observable<FormFieldTypeGeneral[]> {
    const fields: FormFieldTypeGeneral[] = [];
    const evaluatorMap = this.formFieldEvaluatorService.evaluators;
    Object.keys(evaluatorMap).filter(typeID => FormFieldTypeGeneral.isSupported(typeID))
                             .forEach(typeID => {
                               const evaluator = evaluatorMap[typeID];
                               const type = evaluator.provides.find(t => t.id === typeID);
                               if (!type) { return; }
                               fields.push(BackendIntegrationSerializer.deserializeGeneralFieldType(type));
                             });
    return of(fields);
  }
}
